import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";

import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import toast from "react-hot-toast";
import { URL } from "../config";
import { useOutletContext } from "react-router-dom";

const AssignTable = () => {
  const { setLoading } = useOutletContext();
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");


  const fetchAssignData = async () => {
    setLoading(true); 
  try {
    
    const response = await axios.get(`${URL}/assigntasks`);
    const assignData = response.data;

    
    const techRes = await axios.get(`${URL}/technicians`);
    const technicians = techRes.data; 

   
    const data = assignData.map((item) => {
      const tech = technicians.find((t) => t.technicianId === item.fktechnicianId);
      return {
        technicianName: tech ? tech.technicianName : "N/A",
        requestType: item.requestTitle || "N/A",
        customerName: item.customerName || "N/A",
        mobileNo: item.mobileNo || "N/A",
        address: item.address || "N/A",
        status: item.status || "Pending",
      };
    });

    setDataSource(data);
  } catch (error) {
    console.log("Error fetching assign data:", error);
    toast.error("Failed to load Assign List");
  }
  setLoading(false);
};

useEffect(() => {
  fetchAssignData();
}, []);


  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Assign List");

    sheet.columns = [
      { header: "Technician Name", key: "technicianName", width: 30 },
      { header: "Request Type", key: "requestType", width: 25 },
      { header: "Customer Name", key: "customerName", width: 25 },
      { header: "Mobile No", key: "mobileNo", width: 20 },
      { header: "Address", key: "address", width: 40 },
      { header: "Status", key: "status", width: 20 },
    ];

    dataSource.forEach((row) => sheet.addRow(row));

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Assign_List.xlsx");
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });

    autoTable(doc, {
      head: [
        [
          "Technician Name",
          "Request Type",
          "Customer Name",
          "Mobile No",
          "Address",
          "Status",
        ],
      ],
      body: dataSource.map((item) => [
        item.technicianName,
        item.requestType,
        item.customerName,
        item.mobileNo,
        item.address,
        item.status,
      ]),
    });

    doc.save("Assign_List.pdf");
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    const rows = dataSource
      .map(
        (item) => `
      <tr>
        <td>${item.technicianName}</td>
        <td>${item.requestType}</td>
        <td>${item.customerName}</td>
        <td>${item.mobileNo}</td>
        <td>${item.address}</td>
        <td>${item.status}</td>
      </tr>`
      )
      .join("");

    printWindow.document.write(`
      <html>
      <head>
          <title>Assign List</title>
      </head>
      <body>
          <h2 style="text-align:center">Assign List</h2>
          <table border="1" width="100%" cellpadding="10">
              <thead>
                  <tr>
                      <th>Technician Name</th>
                      <th>Request Type</th>
                      <th>Customer Name</th>
                      <th>Mobile No</th>
                      <th>Address</th>
                      <th>Status</th>
                  </tr>
              </thead>
              <tbody>
                  ${rows}
              </tbody>
          </table>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  const columns = useMemo(
    () => [
      { accessorKey: "technicianName", header: "Technician Name", size: 180 },
      { accessorKey: "requestType", header: "Request Type", size: 150 },
      { accessorKey: "customerName", header: "Customer Name", size: 180 },
      { accessorKey: "mobileNo", header: "Mobile No", size: 150 },
      {
        accessorKey: "address",
        header: "Address",
        size: 260,
        Cell: ({ row }) => (
          <span
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.original.address}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 130,
        Cell: ({ row }) => (
          <span
            style={{
              padding: "5px 12px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: 600,
              background:
                row.original.status === "Pending" ? "#fff2cc" : "#d7ffd9",
              color: row.original.status === "Pending" ? "#b57f00" : "green",
            }}
          >
            {row.original.status}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#ffffff",
        marginTop: "20px",
        p: { xs: 1, md: 0 },
        overflow: "hidden",
      }}
    >
 
      <Box
        sx={{
          fontWeight: 700,
          fontSize: "22px",
          color: "#174253",
          letterSpacing: 0.5,
          borderLeft: "5px solid #174253",
          paddingLeft: "12px",
          marginBottom: "20px",
        }}
      >
        Assigned Task List
      </Box>

      <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
        <div style={{ position: "relative", width: "100%", marginTop: "10px" }}>
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "20px",
              left: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              zIndex: 2,
              marginBottom: "10px",
            }}
          >
        
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Tooltip title="Download Excel">
                <IconButton
                  onClick={handleDownloadExcel}
                  sx={{
                    backgroundColor: "#d4f1d4",
                    color: "#174253",
                    "&:hover": { backgroundColor: "#b5e3b5" },
                  }}
                >
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF">
                <IconButton
                  onClick={handleDownloadPDF}
                  sx={{
                    backgroundColor: "#ffe2e2",
                    color: "#d32f2f",
                    "&:hover": { backgroundColor: "#ffcccc" },
                  }}
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton
                  onClick={handlePrint}
                  sx={{
                    backgroundColor: "#e3f2fd",
                    color: "#174253",
                    "&:hover": { backgroundColor: "#cde4fa" },
                  }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </div>

     
            <TextField
              size="small"
              placeholder="Search tasks..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#174253" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "200px", sm: "250px" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "25px",
                  height: "38px",
                  backgroundColor: "#fff",
                  boxShadow: "0 0 6px rgba(0,0,0,0.1)",
                  "& fieldset": { borderColor: "#d0d7de" },
                  "&:hover fieldset": { borderColor: "#174253" },
                },
              }}
            />
          </div>
        </div>

        <MaterialReactTable
          columns={columns}
          data={dataSource.filter((item) =>
            Object.values(item)
              .join(" ")
              .toLowerCase()
              .includes(globalFilter.toLowerCase())
          )}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableColumnFilters={false}
          enableHiding={false}
          enableGlobalFilter={false}
          muiTablePaperProps={{ sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" } }}
          muiTableContainerProps={{
            sx: {
              backgroundColor: "#fff",
              width: "100%",
              overflowX: "auto",
              borderRadius: "12px",
              "&::-webkit-scrollbar": { height: "6px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#e0e3ea", borderRadius: "10px" },
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#e0e0e0ff",
              color: "#393e4f",
              fontWeight: 600,
              fontSize: "16px",
              borderBottom: "0.5px solid #e3e5eb",
              borderRight: "none",
              borderLeft: "none",
              textAlign: "left",
              height: "63px",
              paddingTop: "23px",
              marginTop: "15px",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: "#fff",
              "&:hover": { backgroundColor: "#eff6fe" },
              "& td": {
                fontSize: { xs: "12px", sm: "14px" },
                p: "8px",
                borderBottom: "0.5px solid #e3e5eb",
                borderRight: "none",
                borderLeft: "none",
                height: "54px",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default AssignTable;
